/** The owner must dispose fallback listeners when its request/stream ends. */
export function combineAbortSignals(signals: AbortSignal[]): {
  signal: AbortSignal;
  dispose?: () => void;
} {
  if (typeof AbortSignal.any === "function") {
    return { signal: AbortSignal.any(signals) };
  }

  const controller = new AbortController();
  const listeners = new Map<AbortSignal, () => void>();
  const dispose = () => {
    for (const [signal, listener] of listeners) {
      signal.removeEventListener("abort", listener);
    }
    listeners.clear();
  };
  for (const signal of new Set(signals)) {
    const abort = () => {
      controller.abort(signal.reason);
      dispose();
    };
    if (signal.aborted) {
      abort();
      break;
    }
    listeners.set(signal, abort);
    signal.addEventListener("abort", abort, { once: true });
  }
  return { signal: controller.signal, dispose };
}

/** Keep cancellation connected past headers, through EOF, read error or cancel. */
export function disposeAfterResponse(response: Response, dispose?: () => void): Response {
  if (!dispose) return response;
  if (!response.body) {
    dispose();
    return response;
  }
  const reader = response.body.getReader();
  void reader.closed.then(dispose, dispose);
  const body = new ReadableStream<Uint8Array>({
    async pull(controller) {
      try {
        const { done, value } = await reader.read();
        if (done) controller.close();
        else controller.enqueue(value);
      } catch (error) {
        controller.error(error);
      }
    },
    cancel(reason) {
      dispose();
      return reader.cancel(reason);
    },
  }, { highWaterMark: 0 });
  return new Response(body, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}
